import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, Zap, Loader2, ShieldCheck, X } from "lucide-react";
import Layout from "@/components/Layout";
import client, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const TIER_ACCENT = {
  starter: "#0B132B",
  control: "#D97706",
  operations: "#059669",
};

function StatusBanner({ status }) {
  if (!status) return null;
  const map = {
    checking: { icon: <Loader2 className="h-4 w-4 animate-spin" />, text: "Confirming your payment with Stripe…", cls: "bg-slate-100 text-ink-2 border-slate-300" },
    success: { icon: <Check className="h-4 w-4" />, text: "Payment confirmed — your workspace subscription is now active.", cls: "bg-[#ECFDF3] text-[#059669] border-[#059669]" },
    cancelled: { icon: <X className="h-4 w-4" />, text: "Checkout was cancelled. No charge was made.", cls: "bg-[#FFF5F5] text-[#DC2626] border-[#DC2626]" },
    error: { icon: <X className="h-4 w-4" />, text: "We couldn't confirm the payment. Please check your Stripe email or try again.", cls: "bg-[#FFF5F5] text-[#DC2626] border-[#DC2626]" },
    timeout: { icon: <X className="h-4 w-4" />, text: "Payment status check timed out. If you completed payment, refresh in a moment.", cls: "bg-[#FFFDF9] text-[#D97706] border-[#D97706]" },
  };
  const s = map[status];
  if (!s) return null;
  return (
    <div data-testid="billing-status-banner" className={`flex items-center gap-2 border-2 rounded-md px-4 py-3 text-sm font-semibold mb-6 ${s.cls}`}>
      {s.icon} {s.text}
    </div>
  );
}

export default function Billing() {
  const [params, setParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deploying, setDeploying] = useState("");
  const [banner, setBanner] = useState("");

  const loadPlans = useCallback(() => {
    client.get("/billing/plans")
      .then((r) => { setPlans(r.data.plans); setCurrent(r.data.current); })
      .catch(() => toast.error("Could not load plans."))
      .finally(() => setLoading(false));
  }, []);

  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    const MAX = 6;
    if (attempts >= MAX) { setBanner("timeout"); return; }
    try {
      const r = await client.get(`/billing/checkout-status/${sessionId}`);
      if (r.data.payment_status === "paid") {
        setBanner("success");
        toast.success("Subscription activated.");
        loadPlans();
        return;
      }
      if (r.data.status === "expired") { setBanner("error"); return; }
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch (e) {
      setBanner("error");
    }
  }, [loadPlans]);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  useEffect(() => {
    const sessionId = params.get("session_id");
    const cancelled = params.get("checkout");
    if (sessionId) {
      setBanner("checking");
      pollStatus(sessionId);
      params.delete("session_id");
      setParams(params, { replace: true });
    } else if (cancelled === "cancelled") {
      setBanner("cancelled");
      params.delete("checkout");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deploy = async (planId) => {
    setDeploying(planId);
    try {
      const r = await client.post("/billing/create-checkout-session", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout.");
      setDeploying("");
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        <div className="mb-2 flex items-center gap-2">
          <span className="text-[10px] font-mono font-bold text-[#D97706] tracking-widest uppercase">Workspace Billing</span>
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black text-[#0B132B] tracking-tight">Deploy your plan</h1>
        <p className="text-sm text-ink-2 mt-1 mb-6 max-w-2xl">Choose a subscription tier for this workspace. Payment is processed securely through Stripe — you'll be redirected to a hosted checkout screen.</p>

        <StatusBanner status={banner} />

        {current?.subscription_status === "active" && (
          <div data-testid="current-plan-banner" className="flex items-center gap-3 bg-[#0B132B] text-white rounded-md px-4 py-3 mb-6 shadow-[2px_2px_0px_0px_#0B132B]">
            <ShieldCheck className="h-5 w-5 text-[#059669]" />
            <div className="text-sm">
              <span className="font-black">{current.plan}</span>
              <span className="text-white/60"> · Active</span>
              {current.current_period_end && (
                <span className="text-white/50 text-xs ml-2">renews {new Date(current.current_period_end).toLocaleDateString()}</span>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-ink-2 py-10">Loading plans…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="pricing-grid">
            {plans.map((p) => {
              const accent = TIER_ACCENT[p.tier] || "#0B132B";
              const isCurrent = current?.plan_tier === p.tier && current?.subscription_status === "active";
              const featured = p.tier === "control";
              return (
                <div key={p.id} data-testid={`plan-card-${p.tier}`}
                  className={`relative bg-white border-2 rounded-md p-6 flex flex-col ${featured ? "border-[#D97706] shadow-[3px_3px_0px_0px_#D97706]" : "border-[#0B132B] shadow-[2px_2px_0px_0px_#0B132B]"}`}>
                  {featured && (
                    <span className="absolute -top-3 left-6 bg-[#D97706] text-white text-[9px] font-black tracking-widest uppercase px-2 py-1 rounded flex items-center gap-1"><Zap className="h-3 w-3" /> Most Popular</span>
                  )}
                  <h3 className="font-heading text-lg font-black text-[#0B132B] tracking-tight">{p.name}</h3>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-4xl font-black" style={{ color: accent }}>${Number(p.monthly_price).toLocaleString()}</span>
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">/mo</span>
                  </div>
                  <p className="text-[11px] font-mono font-bold text-slate-400 uppercase tracking-wider mt-1">{p.job_volume_limit} · up to {p.user_limit} users</p>

                  <ul className="mt-4 space-y-2 flex-1">
                    {(p.included_features || []).map((f) => (
                      <li key={f} className="flex items-start gap-2 text-[13px] text-ink-2">
                        <Check className="h-4 w-4 shrink-0 mt-0.5" style={{ color: accent }} /> {f}
                      </li>
                    ))}
                  </ul>

                  <button
                    data-testid={`deploy-plan-${p.tier}`}
                    disabled={!!deploying || isCurrent}
                    onClick={() => deploy(p.id)}
                    className={`mt-6 w-full h-11 rounded-md font-black text-sm tracking-wide uppercase text-white flex items-center justify-center gap-2 transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-[2px_2px_0px_0px_#0B132B] hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px]`}
                    style={{ backgroundColor: isCurrent ? "#059669" : accent }}>
                    {deploying === p.id ? <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting…</>
                      : isCurrent ? <>Current Plan</>
                      : <>Deploy To Workspace</>}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-[11px] text-slate-400 mt-6 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Secured by Stripe. Prices are set server-side; your workspace is billed monthly.</p>
      </div>
    </Layout>
  );
}
